import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import ActionButton from '../ActionButton';
import MenuTitleBar from '../MenuTitleBar';
import { ConfigurationContext } from '../../ContextProviders/ConfigurationProvider';
import Divider from '@material-ui/core/Divider';

const StyledDialogContent = withStyles({
  root: {
    padding: '24px 24px 24px 24px',
  },
})(DialogContent);

const LabelTypography = withStyles({
  root: {
    fontWeight: 'bold',
  },
})(Typography);

const ComponentOption = withStyles((theme) => ({
  root: {
    display: 'inline-flex',
    gap: 6,
    position: 'relative',
    marginRight: 6,
    flexWrap: 'wrap',
    '&:after': {
      content: '","',
      fontFamily: theme.typography.fontFamily,
      lineHeight: theme.typography.body1.lineHeight,
      fontSize: theme.typography.body1.fontSize,
    },
    '&:last-child:after': {
      display: 'none',
    },
  },
  value: {
    whiteSpace: 'nowrap',
    '&:after': {
      content: '","',
      fontFamily: theme.typography.fontFamily,
      lineHeight: theme.typography.body1.lineHeight,
      fontSize: theme.typography.body1.fontSize,
    },
    '&:last-child:after': {
      display: 'none',
    },
  },
}))(({ label, value, classes }) => (
  <div className={classes.root}>
    {label && <LabelTypography variant="body1">{label}</LabelTypography>}
    {value instanceof Array ? (
      value.map((value) => (
        <Typography key={value} variant="body1" className={classes.value}>
          {value}
        </Typography>
      ))
    ) : (
      <Typography variant="body1">{value}</Typography>
    )}
  </div>
));

const ComponentGroup = withStyles({
  divider: {
    marginTop: 16,
    marginBottom: 16,
  },
})(({ divider = true, children, classes, ...props }) => (
  <>
    <div {...props}>{children}</div>
    {divider && <Divider className={classes.divider} />}
  </>
));

const componentsPerItem = {
  warehouse: ['roofType', 'wallType', 'roofWindowType', 'rainGutter'],
  tent: ['roofType', 'wallType'],
};

const RequestOffer = () => {
  const customData = useContext(ConfigurationContext);
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const data = customData.getData();

  const objectGroups = Object.values(
    data.objects.reduce((groups, item) => {
      const group = item.option?.attributes?.find(({ identifier }) => identifier === 'type')?.values?.[0];
      if (!groups[group.value]) {
        groups[group.value] = {
          groupTitle: group.translated_value,
          options: {},
        };
      }
      const itemIdentifier = [
        item.option.identifier,
        ...(item.children || []).map(({ option }) => option.identifier),
      ].join('+');
      if (!groups[group.value].options[itemIdentifier]) {
        groups[group.value].options[itemIdentifier] = {
          title:
            item.option.title +
            (item.children ? ` (+ ${item.children.map(({ option }) => option.title).join(', ')})` : ''),
          amount: 1,
        };
      } else {
        groups[group.value].options[itemIdentifier].amount++;
      }
      return groups;
    }, {})
  ).map((group) => ({ ...group, options: Object.values(group.options) }));

  return (
    <>
      <ActionButton label={t('myConfiguration.actionButtonText')} onClick={handleClickOpen} />

      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <MenuTitleBar title={t('myConfiguration.dialogTitle')} onClose={handleClose} />
        <StyledDialogContent>
          <ComponentGroup>
            {['width', 'length', 'height'].map((dim) => (
              <ComponentOption key={dim} label={t(`dimensions.${dim}`)} value={`${data[dim]} m`} />
            ))}
          </ComponentGroup>
          <ComponentGroup>
            {componentsPerItem[data.itemIdentifier].map((componentName) => (
              <ComponentOption
                key={componentName}
                label={t(`myConfiguration.${componentName}`)}
                value={
                  typeof data[componentName] === 'boolean'
                    ? t(`myConfiguration.${data[componentName] ? 'yes' : 'no'}`)
                    : data[componentName]?.option?.title ||
                      data[componentName]?.title ||
                      t(`myConfiguration.notSelectedLabel`)
                }
              />
            ))}
          </ComponentGroup>
          {objectGroups.map((group) => (
            <ComponentGroup key={group.groupTitle}>
              <ComponentOption
                label={group.groupTitle}
                value={group.options.map(({ title, amount }) => `${amount}x ${title}`)}
              />
            </ComponentGroup>
          ))}
        </StyledDialogContent>
      </Dialog>
    </>
  );
};

export default RequestOffer;
